import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, getAuthHeaders } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  LogOut, Users, MessageSquare, Calendar, Plus, Trash2, Edit,
  Star, LayoutDashboard
} from 'lucide-react';

function api() { return axios.create({ baseURL: API, headers: getAuthHeaders(), withCredentials: true }); }

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [showAddTestimonial, setShowAddTestimonial] = useState(false);
  const [newT, setNewT] = useState({ name: '', role: '', company: '', content: '', rating: 5, image_url: '', approved: true });

  const fetchAll = useCallback(async () => {
    try {
      const [l, b, t] = await Promise.all([
        api().get('/admin/leads'),
        api().get('/admin/bookings'),
        api().get('/admin/testimonials')
      ]);
      setLeads(l.data);
      setBookings(b.data);
      setTestimonials(t.data);
    } catch { toast.error('Failed to load data'); }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate('/admin/login');
    else if (user) fetchAll();
  }, [user, loading, navigate, fetchAll]);

  const updateLeadStatus = async (id, status) => {
    try {
      await api().patch(`/admin/leads/${id}/status`, { status });
      setLeads(p => p.map(l => l.id === id ? { ...l, status } : l));
      toast.success('Lead status updated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteLead = async (id) => {
    try {
      await api().delete(`/admin/leads/${id}`);
      setLeads(p => p.filter(l => l.id !== id));
      toast.success('Lead deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await api().patch(`/admin/bookings/${id}/status`, { status });
      setBookings(p => p.map(b => b.id === id ? { ...b, status } : b));
      toast.success('Booking status updated');
    } catch { toast.error('Failed to update'); }
  };

  const addTestimonial = async () => {
    if (!newT.name || !newT.content) { toast.error('Name and content required'); return; }
    try {
      const { data } = await api().post('/admin/testimonials', newT);
      setTestimonials(p => [data, ...p]);
      setNewT({ name: '', role: '', company: '', content: '', rating: 5, image_url: '', approved: true });
      setShowAddTestimonial(false);
      toast.success('Testimonial added');
    } catch { toast.error('Failed to add'); }
  };

  const updateTestimonial = async () => {
    if (!editTestimonial) return;
    try {
      await api().put(`/admin/testimonials/${editTestimonial.id}`, editTestimonial);
      setTestimonials(p => p.map(t => t.id === editTestimonial.id ? editTestimonial : t));
      setEditTestimonial(null);
      toast.success('Testimonial updated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api().delete(`/admin/testimonials/${id}`);
      setTestimonials(p => p.filter(t => t.id !== id));
      toast.success('Testimonial deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-blue-600">Loading...</div></div>;
  if (!user) return null;

  const statusBadge = (status) => {
    const colors = { new: 'bg-blue-50 text-blue-600', contacted: 'bg-amber-50 text-amber-600', converted: 'bg-green-50 text-green-600', closed: 'bg-red-50 text-red-600', pending: 'bg-amber-50 text-amber-600', confirmed: 'bg-green-50 text-green-600', completed: 'bg-blue-50 text-blue-600', cancelled: 'bg-red-50 text-red-600' };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 px-6 sm:px-12 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard size={20} className="text-blue-600" />
            <h1 className="font-['Outfit'] text-lg font-semibold text-slate-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-sm hidden sm:block">{user.email}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 text-slate-500 hover:text-red-500 transition-colors text-sm" data-testid="admin-logout-btn">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Leads', count: leads.length, icon: Users, color: 'text-blue-600 bg-blue-50' },
            { label: 'New Leads', count: leads.filter(l => l.status === 'new').length, icon: MessageSquare, color: 'text-green-600 bg-green-50' },
            { label: 'Bookings', count: bookings.length, icon: Calendar, color: 'text-amber-600 bg-amber-50' },
            { label: 'Testimonials', count: testimonials.length, icon: Star, color: 'text-purple-600 bg-purple-50' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-slate-200" data-testid={`stat-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-xs font-medium">{s.label}</span>
                <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center`}>
                  <s.icon size={16} />
                </div>
              </div>
              <div className="text-slate-900 text-2xl font-['Outfit'] font-bold">{s.count}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-white border border-slate-200 mb-6">
            <TabsTrigger value="leads" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-testimonials">Testimonials</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="leads-table-container">
              {leads.length === 0 ? (
                <div className="p-12 text-center text-slate-400">No leads yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-slate-500">Name</TableHead>
                      <TableHead className="text-slate-500">Email</TableHead>
                      <TableHead className="text-slate-500 hidden md:table-cell">Service</TableHead>
                      <TableHead className="text-slate-500">Status</TableHead>
                      <TableHead className="text-slate-500 hidden lg:table-cell">Date</TableHead>
                      <TableHead className="text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map(l => (
                      <TableRow key={l.id} className="border-slate-100 hover:bg-slate-50">
                        <TableCell className="text-slate-900 font-medium text-sm">{l.full_name}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{l.email}</TableCell>
                        <TableCell className="text-slate-500 text-sm hidden md:table-cell">{l.service || '-'}</TableCell>
                        <TableCell>{statusBadge(l.status)}</TableCell>
                        <TableCell className="text-slate-500 text-xs hidden lg:table-cell">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Select onValueChange={v => updateLeadStatus(l.id, v)}>
                              <SelectTrigger className="h-7 w-24 bg-transparent border-slate-200 text-xs text-slate-500">
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-slate-200">
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                            <button onClick={() => deleteLead(l.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-lead-${l.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="bookings-table-container">
              {bookings.length === 0 ? (
                <div className="p-12 text-center text-slate-400">No bookings yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-slate-500">Name</TableHead>
                      <TableHead className="text-slate-500">Email</TableHead>
                      <TableHead className="text-slate-500 hidden md:table-cell">Service</TableHead>
                      <TableHead className="text-slate-500 hidden md:table-cell">Date/Time</TableHead>
                      <TableHead className="text-slate-500">Status</TableHead>
                      <TableHead className="text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map(b => (
                      <TableRow key={b.id} className="border-slate-100 hover:bg-slate-50">
                        <TableCell className="text-slate-900 font-medium text-sm">{b.full_name}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{b.email}</TableCell>
                        <TableCell className="text-slate-500 text-sm hidden md:table-cell">{b.service || '-'}</TableCell>
                        <TableCell className="text-slate-500 text-sm hidden md:table-cell">{b.preferred_date || '-'} {b.preferred_time || ''}</TableCell>
                        <TableCell>{statusBadge(b.status)}</TableCell>
                        <TableCell className="text-right">
                          <Select onValueChange={v => updateBookingStatus(b.id, v)}>
                            <SelectTrigger className="h-7 w-28 bg-transparent border-slate-200 text-xs text-slate-500">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="testimonials">
            <div className="mb-4 flex justify-end">
              <button onClick={() => setShowAddTestimonial(true)} className="btn-blue px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="add-testimonial-btn">
                <Plus size={16} /> Add Testimonial
              </button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="testimonials-table-container">
              {testimonials.length === 0 ? (
                <div className="p-12 text-center text-slate-400">No testimonials yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-slate-500">Name</TableHead>
                      <TableHead className="text-slate-500 hidden md:table-cell">Role</TableHead>
                      <TableHead className="text-slate-500 hidden lg:table-cell">Content</TableHead>
                      <TableHead className="text-slate-500">Rating</TableHead>
                      <TableHead className="text-slate-500">Status</TableHead>
                      <TableHead className="text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {testimonials.map(t => (
                      <TableRow key={t.id} className="border-slate-100 hover:bg-slate-50">
                        <TableCell className="text-slate-900 font-medium text-sm">{t.name}</TableCell>
                        <TableCell className="text-slate-500 text-sm hidden md:table-cell">{t.role || '-'}</TableCell>
                        <TableCell className="text-slate-500 text-sm hidden lg:table-cell max-w-xs truncate">{t.content}</TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} size={12} className="text-blue-500 fill-blue-500" />)}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={t.approved ? 'default' : 'secondary'} className={t.approved ? 'bg-green-50 text-green-600 border-0' : 'bg-red-50 text-red-600 border-0'}>
                            {t.approved ? 'Approved' : 'Hidden'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setEditTestimonial({...t})} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`edit-testimonial-${t.id}`}>
                              <Edit size={14} />
                            </button>
                            <button onClick={() => deleteTestimonial(t.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-testimonial-${t.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showAddTestimonial} onOpenChange={setShowAddTestimonial}>
        <DialogContent className="bg-white border-slate-200 sm:max-w-md" data-testid="add-testimonial-modal">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-['Outfit']">Add Testimonial</DialogTitle>
            <DialogDescription className="text-slate-500">Add a new client testimonial</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-slate-900 text-sm mb-1.5 block">Name *</Label><Input value={newT.name} onChange={e => setNewT(p => ({...p, name: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="new-testimonial-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-900 text-sm mb-1.5 block">Role</Label><Input value={newT.role} onChange={e => setNewT(p => ({...p, role: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="new-testimonial-role" /></div>
              <div><Label className="text-slate-900 text-sm mb-1.5 block">Company</Label><Input value={newT.company} onChange={e => setNewT(p => ({...p, company: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="new-testimonial-company" /></div>
            </div>
            <div><Label className="text-slate-900 text-sm mb-1.5 block">Content *</Label><Textarea value={newT.content} onChange={e => setNewT(p => ({...p, content: e.target.value}))} rows={3} className="bg-slate-50 border-slate-200 text-slate-900 resize-none" data-testid="new-testimonial-content" /></div>
            <div><Label className="text-slate-900 text-sm mb-1.5 block">Image URL</Label><Input value={newT.image_url} onChange={e => setNewT(p => ({...p, image_url: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="new-testimonial-image" /></div>
            <button onClick={addTestimonial} className="btn-blue w-full py-3 rounded-xl text-sm" data-testid="save-new-testimonial-btn">Save Testimonial</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTestimonial} onOpenChange={() => setEditTestimonial(null)}>
        <DialogContent className="bg-white border-slate-200 sm:max-w-md" data-testid="edit-testimonial-modal">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-['Outfit']">Edit Testimonial</DialogTitle>
            <DialogDescription className="text-slate-500">Update testimonial details</DialogDescription>
          </DialogHeader>
          {editTestimonial && (
            <div className="space-y-4 mt-2">
              <div><Label className="text-slate-900 text-sm mb-1.5 block">Name</Label><Input value={editTestimonial.name} onChange={e => setEditTestimonial(p => ({...p, name: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="edit-testimonial-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-900 text-sm mb-1.5 block">Role</Label><Input value={editTestimonial.role} onChange={e => setEditTestimonial(p => ({...p, role: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="edit-testimonial-role" /></div>
                <div><Label className="text-slate-900 text-sm mb-1.5 block">Company</Label><Input value={editTestimonial.company} onChange={e => setEditTestimonial(p => ({...p, company: e.target.value}))} className="bg-slate-50 border-slate-200 text-slate-900 h-10" data-testid="edit-testimonial-company" /></div>
              </div>
              <div><Label className="text-slate-900 text-sm mb-1.5 block">Content</Label><Textarea value={editTestimonial.content} onChange={e => setEditTestimonial(p => ({...p, content: e.target.value}))} rows={3} className="bg-slate-50 border-slate-200 text-slate-900 resize-none" data-testid="edit-testimonial-content" /></div>
              <div className="flex items-center gap-3">
                <Label className="text-slate-900 text-sm">Approved</Label>
                <button onClick={() => setEditTestimonial(p => ({...p, approved: !p.approved}))} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${editTestimonial.approved ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`} data-testid="edit-testimonial-approved-toggle">
                  {editTestimonial.approved ? 'Approved' : 'Hidden'}
                </button>
              </div>
              <button onClick={updateTestimonial} className="btn-blue w-full py-3 rounded-xl text-sm" data-testid="save-edit-testimonial-btn">Update Testimonial</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
